import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { EatalyProfileDropdownPanel } from './eataly-profile-dropdown-panel';
import { EatalyGuestProfileDropdownPanel } from './eataly-guest-profile-dropdown-panel';
import { EatalyProfilePopoverCaret } from './eataly-profile-popover-caret';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from 'src/types/config';
import { useEatalyHeaderChrome } from '../eataly-header-chrome-context';

const EATALY_PROFILE_SURFACE_SHADOW = 'shadow-[0px_4px_12px_rgba(0,0,0,0.05)]';
/** Cap at design width, but never exceed Radix collision space (avoids right overflow on narrow viewports). */
const PROFILE_POPOVER_WIDTH_CLASS =
    'w-[min(361px,var(--radix-popper-available-width,calc(100vw-32px)))] max-w-[var(--radix-popper-available-width,calc(100vw-32px))] min-w-0!';

export interface EatalyHeaderAccountMenuProps {
    isAuthenticated: boolean;
    trigger: ReactElement;
}

function EatalyProfilePopoverSurface({ children }: { children: ReactNode }): ReactElement {
    return (
        <div className="flex w-full flex-col items-stretch">
            <div className="relative z-[4] right-[40px] flex w-full shrink-0 justify-end">
                <div className="relative -bottom-1">
                    <EatalyProfilePopoverCaret className="-bottom-2" />
                    <div className="relative -top-0.5 h-[2px] w-6 bg-[var(--neutral-white)]" />
                </div>
            </div>
            <div
                className={cn(
                    'relative z-[1] -mt-px overflow-hidden rounded-[24px] border border-[var(--sand-95)] bg-[var(--neutral-white)]',
                    EATALY_PROFILE_SURFACE_SHADOW
                )}>
                {children}
            </div>
        </div>
    );
}

export function EatalyHeaderAccountMenu({ isAuthenticated, trigger }: EatalyHeaderAccountMenuProps): ReactElement {
    const { headerHidden } = useEatalyHeaderChrome();
    const [open, setOpen] = useState(false);
    const config = useConfig<AppConfig>();
    const isSocialLoginAvailable = Boolean(
        config.features.socialLogin?.enabled && config.features.socialLogin.providers.length > 0
    );

    useEffect(() => {
        if (headerHidden) {
            setOpen(false);
        }
    }, [headerHidden]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleCloseAutoFocus = (event: Event) => {
        if (headerHidden) {
            event.preventDefault();
        }
    };

    const popoverContentProps = {
        className: cn(
            PROFILE_POPOVER_WIDTH_CLASS,
            'overflow-visible border-0 bg-transparent p-0 shadow-none z-[70]',
            'data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100',
            headerHidden && 'pointer-events-none invisible'
        ),
        align: 'end' as const,
        arrowPadding: 12,
        alignOffset: -40,
        sideOffset: 5,
        avoidCollisions: true,
        collisionPadding: 16,
        onCloseAutoFocus: handleCloseAutoFocus,
    };

    if (!isSocialLoginAvailable && !isAuthenticated) {
        return <>{trigger}</>;
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent {...popoverContentProps}>
                <EatalyProfilePopoverSurface>
                    {isAuthenticated ? (
                        <EatalyProfileDropdownPanel onNavigate={handleClose} />
                    ) : (
                        <EatalyGuestProfileDropdownPanel onNavigate={handleClose} />
                    )}
                </EatalyProfilePopoverSurface>
            </PopoverContent>
        </Popover>
    );
}
